import { useEffect, useState } from 'react';

import { debounce } from 'lodash';

import { Divider } from '@components/index';

import ChipListWithSearch from './ChipListWithSearch';
import { CreateNewLabel } from './CreateNewLabel';
import { DeleteLabelModal } from './DeleteLabelModal';
import { useDeleteLabel } from './hooks/use-delete-label';
import { useGetLabels } from './hooks/use-get-labels';
import { useUpdateLabel } from './hooks/use-update-label';
import { LabelMetadataModal } from './LabelMetadataModal';
import { RemoveLabelConfirmationModal } from './RemoveLabelConfirmationModal';
import {
  ContentModalActionType,
  Label,
  LabelItem,
  LabelModalData,
} from './types';

type Props = {
  onSelectOption: (option: any) => void;
  selectedOptions?: Record<string, any>;
  onClearAllOptions?: () => void;
  onLabelEditSuccess?: (data: LabelModalData, selectedLabel: Label) => void;
  onLabelDeleteSuccess?: (label: Label) => void;
  isMultipleItemSelected?: boolean;
  isFilterMode?: boolean;
  isAssetInherited?: boolean;
  isAssetLocked?: boolean;
};

const LabelsPopper = ({
  onSelectOption,
  selectedOptions,
  onClearAllOptions,
  onLabelDeleteSuccess,
  onLabelEditSuccess,
  isFilterMode = false,
  isAssetInherited = false,
  isAssetLocked = false,
}: Props) => {
  const [labelSearchInput, setLabelSearchInput] = useState('');
  const [selectedAction, setSelectedAction] =
    useState<ContentModalActionType | null>(null);
  const [selectedLabel, setSelectedLabel] = useState<Label | null>(null);
  const [isRemoveLabelModalOpen, setIsRemoveLabelModalOpen] = useState(false);

  const {
    labelList,
    isLabelListLoading,
    hasNextLabelPage,
    fetchNextLabelPage,
  } = useGetLabels({
    searchTerm: labelSearchInput,
  });

  const {
    mutateAsync: deleteLabel,
    isLoading: isDeletingLabel,
    isSuccess: isLabelDeleted,
  } = useDeleteLabel();

  const {
    mutateAsync: updataLabel,
    isLoading: isUpdatingLabel,
    isSuccess: isLabelUpdated,
  } = useUpdateLabel();

  const handleLabelSearch = (e: React.ChangeEvent<HTMLInputElement>) => {
    setLabelSearchInput(e.target.value);
  };

  const handleUpdateSelectedItems = (item: LabelItem) => {
    setLabelSearchInput('');
    onSelectOption(item);
  };

  const debouncedLabelSearch = debounce(e => {
    handleLabelSearch(e);
  }, 500);

  const handleActionClick = (data: Label, action: ContentModalActionType) => {
    setSelectedAction(action);
    setSelectedLabel(data);
  };

  const handleCancelAction = () => {
    setSelectedAction(null);
    setSelectedLabel(null);
  };

  const handleDeleteLabel = async () => {
    if (selectedLabel) {
      deleteLabel(selectedLabel.id);
    }
  };

  const handleSaveLabel = async (data: LabelModalData) => {
    if (selectedLabel) {
      try {
        await updataLabel({
          labelId: selectedLabel.id,
          ...data,
        });
        onLabelEditSuccess?.(data, selectedLabel);
      } catch (error) {
        console.error(error);
      } finally {
        handleCancelAction();
      }
    }
  };

  useEffect(() => {
    if (isLabelDeleted || isLabelUpdated) {
      handleCancelAction();
    }
    if (isLabelDeleted && selectedLabel) {
      onLabelDeleteSuccess?.(selectedLabel);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isLabelDeleted, isLabelUpdated]);

  return (
    <div className="bg-modal-surface-default w-[356px] text-text-primary px-2 py-3 flex flex-col gap-0.5 rounded-lg border border-dark-4 shadow-lg">
      <div className="px-1.5 mb-1 text-text-secondary text-xs">
        Manage labels
      </div>
      <ChipListWithSearch
        items={Object.values(labelList || {})}
        shouldClearSearch={labelSearchInput.length === 0}
        handleSearch={debouncedLabelSearch}
        onSelectOption={onSelectOption}
        onClearAllOptions={
          onClearAllOptions &&
          (isFilterMode
            ? () => onClearAllOptions?.()
            : () => setIsRemoveLabelModalOpen(true))
        }
        selectedOptions={selectedOptions}
        fetchMoreOptions={fetchNextLabelPage}
        optionsHasNextPage={hasNextLabelPage}
        isLoadingOptions={isLabelListLoading}
        isClientSearch={false}
        handleActionClick={handleActionClick}
        isFilterMode={isFilterMode}
        isAssetInherited={isAssetInherited}
        isAssetLocked={isAssetLocked}
      />
      <Divider />
      <CreateNewLabel
        updateSelectedItems={handleUpdateSelectedItems}
        isFilterMode={isFilterMode}
        isAssetLocked={isAssetLocked}
      />
      {selectedLabel && (
        <>
          <DeleteLabelModal
            isOpen={selectedAction === ContentModalActionType.DELETE_LABEL}
            labelId={selectedLabel.id}
            isDeleting={isDeletingLabel}
            handleCancel={handleCancelAction}
            handleDelete={handleDeleteLabel}
          />
          <LabelMetadataModal
            data={selectedLabel}
            isOpen={
              selectedAction !== null &&
              [
                ContentModalActionType.EDIT_LABEL,
                ContentModalActionType.LABEL_METADATA,
              ].includes(selectedAction)
            }
            type={selectedAction}
            handleCancel={handleCancelAction}
            handleSave={handleSaveLabel}
            isLoading={isUpdatingLabel}
            isReadonly={selectedLabel?.parentId ? true : false}
          />
        </>
      )}
      {onClearAllOptions && (
        <RemoveLabelConfirmationModal
          isOpen={isRemoveLabelModalOpen}
          setIsOpen={setIsRemoveLabelModalOpen}
          handleConfirmRemove={onClearAllOptions}
        />
      )}
    </div>
  );
};

export default LabelsPopper;
